import { useQuery } from '@tanstack/react-query';

import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import type { ApiResponse } from '../../Http';
import { buildGetRequest } from '../../Http/requestTemplates';
import useHttp from '../../Http/useHttp';
import { settings } from '../../helpers';

const PAYMENTS_QUERY = 'EcomUri/payments/:id';

interface PayymentListItemSetting {
  max: number;
  min: number;
  step: number;
  default: number;
  current: number;
  fixed: boolean;
}

export interface PaymentListItem {
  monthlyCost: number;
  interest: number;
  effectiveInterest: number;
  setupFee: number;
  registrationFee: number | null;
  administrationFee: number;
  totalCreditCost: number;
  totalResidualValue: number;
  totalVehicleCost: number;
  loanAmount: number;
  vehiclePrice: number;
  residual: PayymentListItemSetting | null;
  duration: PayymentListItemSetting;
  downPayment: PayymentListItemSetting;
  financialInstitution: string;
  financialOptionId: string;
  link: string;
  linkText: string;
  imageUrl: string;
  useCreditScoring: boolean;
  financialProductCode: string | null;
  privacyPolicyUrl: string | null;
  name: string;
  description: string | null;
  creditApplicationUrl: string | null;
}

const usePayments = (
  id?: string,
  branchId?: string,
): [PaymentListItem[] | undefined, RequestStateQuery] => {
  const { httpJson } = useHttp();

  const { data, isPending, isSuccess, isError } = useQuery<ApiResponse<PaymentListItem[]>>({
    queryKey: [PAYMENTS_QUERY, id, branchId],
    queryFn: () =>
      httpJson<ApiResponse<PaymentListItem[]>>(
        `${settings.get('EcomUri')}/payment/${id}/list?branchId=${branchId}`,
        buildGetRequest(),
      ),

    retry: false,
    refetchOnWindowFocus: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!id && !!branchId,
  });

  return [data?.response, { isLoading: isPending, isSuccess, isError }];
};

export default usePayments;
